/**
 * Sistema de Permisos Centralizado
 * 
 * Este archivo define todos los permisos del sistema de forma centralizada
 * para facilitar el mantenimiento y las modificaciones futuras.
 */

export type Role = 'admin' | 'management' | 'client' | 'establishment';

export interface PermissionConfig {
  // Permisos de acceso a rutas
  routes: {
    [route: string]: Role[];
  };
  
  // Permisos de acciones
  actions: {
    [action: string]: {
      allowed: Role[];
      denied?: Role[]; // Roles específicos que NO pueden hacer esta acción
    };
  };
  
  // Permisos de visualización (ocultar/mostrar elementos UI)
  ui: {
    [element: string]: {
      visible: Role[];
      hidden?: Role[];
    };
  };
}

export const PERMISSIONS: PermissionConfig = {
  routes: {
    '/admin': ['admin', 'management'],
    '/admin/users': ['admin', 'management'],
    '/management': ['admin', 'management', 'client', 'establishment'],
    '/management/clients': ['admin', 'management'],
    '/management/establishments': ['admin', 'management', 'client', 'establishment'],
    '/management/fonts': ['admin', 'management', 'client', 'establishment'],
    '/management/tariffs': ['admin', 'management', 'client', 'establishment'],
    '/management/banners': ['admin', 'management', 'client', 'establishment'],
    '/management/smart-banners': ['admin', 'management', 'client', 'establishment'],
    '/management/products': ['admin', 'management', 'client', 'establishment'],
    '/management/screens': ['admin', 'management', 'client', 'establishment'],
    '/management/icons': ['admin', 'management', 'client', 'establishment'],
    '/management/monitoring': ['management'],
    '/display': ['establishment']
  },
  
  actions: {
    // Gestión de usuarios
    'users.create': { allowed: ['admin', 'management'] },
    'users.create_admin': { allowed: ['admin', 'management'] }, // Solo admin/management puede crear otros admins
    'users.edit': { allowed: ['admin', 'management'] },
    'users.delete': { allowed: ['admin', 'management'] },
    'users.list': { allowed: ['admin', 'management'] },
    
    // Gestión de clientes
    'clients.create': { allowed: ['admin', 'management'] },
    'clients.edit': { allowed: ['admin', 'management'] },
    'clients.delete': { allowed: ['admin', 'management'] },
    'clients.list': { allowed: ['admin', 'management'] },
    
    // Gestión de establecimientos
    'establishments.create': { allowed: ['admin', 'management'] },
    'establishments.edit': { allowed: ['admin', 'management'] },
    'establishments.delete': { allowed: ['admin', 'management'] },
    'establishments.toggle_active': { allowed: ['admin', 'management'] },
    'establishments.list': { allowed: ['admin', 'management', 'client', 'establishment'] },
    
    // Gestión de pantallas
    'screens.create': { allowed: ['admin', 'management', 'client'], denied: ['establishment'] },
    'screens.edit': { allowed: ['admin', 'management', 'client', 'establishment'] },
    'screens.delete': { allowed: ['management', 'client'] }, // Solo management y client pueden eliminar
    'screens.configure_styles': { allowed: ['admin', 'management', 'client'], denied: ['establishment'] },
    'screens.list': { allowed: ['admin', 'management', 'client', 'establishment'] },
    
    // Otras acciones
    'fonts.create': { allowed: ['admin', 'management'] },
    'fonts.edit': { allowed: ['admin', 'management', 'client', 'establishment'] },
    'fonts.delete': { allowed: ['admin', 'management', 'client', 'establishment'] },
    'fonts.list': { allowed: ['admin', 'management', 'client', 'establishment'] },
    'banners.manage': { allowed: ['admin', 'management', 'client', 'establishment'] },
    'products.manage': { allowed: ['admin', 'management', 'client', 'establishment'] },
    'tariffs.manage': { allowed: ['admin', 'management', 'client', 'establishment'] },
    'icons.manage': { allowed: ['admin', 'management', 'client', 'establishment'] }
  },
  
  ui: {
    // Elementos del dashboard
    'dashboard.clients_card': { visible: ['admin', 'management'] },
    'dashboard.establishments_card': { visible: ['admin', 'management'] },
    'dashboard.users_card': { visible: ['admin', 'management'] },
    'dashboard.users_menu': { visible: ['admin', 'management'] },
    'dashboard.monitoring_card': { visible: ['management'] },
    
    // Botones de acción
    'button.create_client': { visible: ['admin'] },
    'button.create_establishment': { visible: ['admin'] },
    'button.toggle_establishment_active': { visible: ['admin'] },
    'button.create_screen': { visible: ['admin', 'management', 'client'], hidden: ['establishment'] },
    'button.screen_styles_tab': { visible: ['admin', 'management', 'client'], hidden: ['establishment'] },
    
    // Secciones de formularios
    'form.establishment_active_toggle': { visible: ['admin'] },
    'form.screen_styles_section': { visible: ['admin', 'management', 'client'], hidden: ['establishment'] }
  }
};

/**
 * Verificar si un rol tiene permiso para una acción
 */
export function canPerformAction(role: Role, action: string): boolean {
  const actionConfig = PERMISSIONS.actions[action];
  if (!actionConfig) return false;
  
  // Si está en la lista de denegados, no puede
  if (actionConfig.denied?.includes(role)) return false;
  
  // Si está en la lista de permitidos, puede
  return actionConfig.allowed.includes(role);
}

/**
 * Verificar si un rol puede ver un elemento UI
 */
export function canViewUI(role: Role, element: string): boolean {
  const uiConfig = PERMISSIONS.ui[element];
  if (!uiConfig) return true; // Por defecto, mostrar si no hay configuración
  
  // Si está en la lista de ocultos, no mostrar
  if (uiConfig.hidden?.includes(role)) return false;
  
  // Si está en la lista de visibles, mostrar
  return uiConfig.visible.includes(role);
}

/**
 * Verificar si un rol puede acceder a una ruta
 */
export function canAccessRoute(role: Role, route: string): boolean {
  const allowedRoles = PERMISSIONS.routes[route];
  if (!allowedRoles) return false;
  return allowedRoles.includes(role);
}

